import { createSlice } from '@reduxjs/toolkit';
import type {
  MySubscription,
  SubscriptionPlan,
  SubscriptionUsageRecord,
  SubscriptionsError,
  FreezeInfo,
  FreezeHistoryRecord,
} from '../api/subscriptions.types';
import {
  fetchMySubscription,
  fetchSubscriptionPlans,
  fetchSubscriptionHistory,
  checkCanUseSubscription,
  fetchFreezeInfo,
  freezeSubscription,
  unfreezeSubscription,
  fetchFreezeHistory,
} from './subscriptions.thunk';

interface SubscriptionsState {
  // My subscription
  mySubscription: MySubscription | null;
  mySubscriptionLoading: boolean;
  mySubscriptionError: SubscriptionsError | null;

  // Plans
  plans: SubscriptionPlan[];
  plansLoading: boolean;
  plansError: SubscriptionsError | null;

  // Usage history
  history: SubscriptionUsageRecord[];
  historyLoading: boolean;
  historyError: SubscriptionsError | null;

  // Can use check
  canUse: boolean;
  canUseReason: string | null;
  canUseLoading: boolean;

  // Freeze info
  freezeInfo: FreezeInfo | null;
  freezeInfoLoading: boolean;
  freezeInfoError: SubscriptionsError | null;

  // Freeze history
  freezeHistory: FreezeHistoryRecord[];
  freezeHistoryLoading: boolean;
  freezeHistoryError: SubscriptionsError | null;

  // Freeze/unfreeze operations
  freezeLoading: boolean;
  freezeError: string | null;
}

const initialState: SubscriptionsState = {
  mySubscription: null,
  mySubscriptionLoading: false,
  mySubscriptionError: null,

  plans: [],
  plansLoading: false,
  plansError: null,

  history: [],
  historyLoading: false,
  historyError: null,

  canUse: false,
  canUseReason: null,
  canUseLoading: false,

  freezeInfo: null,
  freezeInfoLoading: false,
  freezeInfoError: null,

  freezeHistory: [],
  freezeHistoryLoading: false,
  freezeHistoryError: null,

  freezeLoading: false,
  freezeError: null,
};

export const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    clearSubscriptionState: (state) => {
      state.mySubscription = null;
      state.mySubscriptionError = null;
      state.history = [];
      state.historyError = null;
      state.canUse = false;
      state.canUseReason = null;
      state.freezeInfo = null;
      state.freezeInfoError = null;
      state.freezeHistory = [];
      state.freezeHistoryError = null;
      state.freezeError = null;
    },
    clearFreezeError: (state) => {
      state.freezeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch my subscription
      .addCase(fetchMySubscription.pending, (state) => {
        state.mySubscriptionLoading = true;
        state.mySubscriptionError = null;
      })
      .addCase(fetchMySubscription.fulfilled, (state, action) => {
        state.mySubscriptionLoading = false;
        state.mySubscription = action.payload;
        // Update canUse based on subscription
        if (action.payload) {
          state.canUse = action.payload.isUsable;
        } else {
          state.canUse = false;
        }
      })
      .addCase(fetchMySubscription.rejected, (state, action) => {
        state.mySubscriptionLoading = false;
        state.mySubscriptionError = action.payload || { code: 500, message: 'Unknown error' };
      })

      // Fetch plans
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = action.payload || { code: 500, message: 'Unknown error' };
      })

      // Fetch history
      .addCase(fetchSubscriptionHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchSubscriptionHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload;
      })
      .addCase(fetchSubscriptionHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload || { code: 500, message: 'Unknown error' };
      })

      // Check can use
      .addCase(checkCanUseSubscription.pending, (state) => {
        state.canUseLoading = true;
      })
      .addCase(checkCanUseSubscription.fulfilled, (state, action) => {
        state.canUseLoading = false;
        state.canUse = action.payload.canUse;
        state.canUseReason = action.payload.reason;
      })
      .addCase(checkCanUseSubscription.rejected, (state) => {
        state.canUseLoading = false;
        state.canUse = false;
      })

      // Fetch freeze info
      .addCase(fetchFreezeInfo.pending, (state) => {
        state.freezeInfoLoading = true;
        state.freezeInfoError = null;
      })
      .addCase(fetchFreezeInfo.fulfilled, (state, action) => {
        state.freezeInfoLoading = false;
        state.freezeInfo = action.payload;
      })
      .addCase(fetchFreezeInfo.rejected, (state, action) => {
        state.freezeInfoLoading = false;
        state.freezeInfoError = action.payload || { code: 500, message: 'Unknown error' };
      })

      // Freeze subscription
      .addCase(freezeSubscription.pending, (state) => {
        state.freezeLoading = true;
        state.freezeError = null;
      })
      .addCase(freezeSubscription.fulfilled, (state) => {
        state.freezeLoading = false;
        // Subscription will be refetched after freeze
      })
      .addCase(freezeSubscription.rejected, (state, action) => {
        state.freezeLoading = false;
        state.freezeError = action.payload?.message || 'Failed to freeze subscription';
      })

      // Unfreeze subscription
      .addCase(unfreezeSubscription.pending, (state) => {
        state.freezeLoading = true;
        state.freezeError = null;
      })
      .addCase(unfreezeSubscription.fulfilled, (state) => {
        state.freezeLoading = false;
        // Subscription will be refetched after unfreeze
      })
      .addCase(unfreezeSubscription.rejected, (state, action) => {
        state.freezeLoading = false;
        state.freezeError = action.payload?.message || 'Failed to unfreeze subscription';
      })

      // Fetch freeze history
      .addCase(fetchFreezeHistory.pending, (state) => {
        state.freezeHistoryLoading = true;
        state.freezeHistoryError = null;
      })
      .addCase(fetchFreezeHistory.fulfilled, (state, action) => {
        state.freezeHistoryLoading = false;
        state.freezeHistory = action.payload;
      })
      .addCase(fetchFreezeHistory.rejected, (state, action) => {
        state.freezeHistoryLoading = false;
        state.freezeHistoryError = action.payload || { code: 500, message: 'Unknown error' };
      });
  },
});

export const { clearSubscriptionState, clearFreezeError } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
